import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Email APIs
export const emailAPI = {
  process: (data?: { max_emails?: number }) => api.post('/emails/process', data),
  list: (params?: { skip?: number; limit?: number; category?: string; response_status?: string }) => 
    api.get('/emails', { params }),
  getById: (id: string) => api.get(`/emails/${id}`),
  getThread: (threadId: string) => api.get(`/emails/thread/${threadId}`),
};

// Review APIs
export const reviewAPI = {
  getPending: (params?: { skip?: number; limit?: number; category?: string; platform?: string }) =>
    api.get('/review/pending-review', { params }),
  getReviewQueue: (params?: { skip?: number; limit?: number; category?: string; response_status?: string; platform?: string }) =>
    api.get('/review/review-queue', { params }),
  getApproved: (params?: { skip?: number; limit?: number; platform?: string }) =>
    api.get('/review/approved', { params }),
  getById: (id: string) => api.get(`/review/${id}`),
  getOrderData: (id: string) => api.get(`/review/${id}/order-data`),
  updateResponse: (id: string, editedResponse: string) =>
    api.put(`/review/${id}/response`, { edited_response: editedResponse }),
  approve: (id: string, reviewedBy: string) =>
    api.post(`/review/${id}/approve`, { reviewed_by: reviewedBy }),
  approveAndSend: (id: string, reviewedBy: string) =>
    api.post(`/review/${id}/approve-and-send`, { reviewed_by: reviewedBy }),
  reject: (id: string, reviewedBy: string) =>
    api.post(`/review/${id}/reject`, { reviewed_by: reviewedBy }),
  reprocess: (id: string) =>
    api.post(`/review/${id}/reprocess`),
  regenerate: (id: string) =>
    api.post(`/review/${id}/regenerate`),
  getResponseVersions: (id: string) =>
    api.get(`/review/${id}/response-versions`),
  bulkApprove: (emailIds: string[], reviewedBy: string) =>
    api.post('/review/bulk-approve', { email_ids: emailIds, reviewed_by: reviewedBy }),
  bulkReject: (emailIds: string[], reviewedBy: string) =>
    api.post('/review/bulk-reject', { email_ids: emailIds, reviewed_by: reviewedBy }),
  bulkReprocess: (emailIds: string[]) =>
    api.post('/review/bulk-reprocess', emailIds),
  bulkRegenerate: (emailIds: string[]) =>
    api.post('/review/bulk-regenerate', emailIds),
  send: (id: string) => api.post(`/review/${id}/send`),
  sendAll: () => api.post('/review/send-all'),
  getStats: () => api.get('/review/stats/review'),
};

// Feedback APIs
export const feedbackAPI = {
  submit: (emailId: string, feedbackType: 'upvote' | 'downvote') =>
    api.post(`/feedback/${emailId}/feedback`, { feedback_type: feedbackType }),
  get: (emailId: string) => api.get(`/feedback/${emailId}/feedback`),
  remove: (emailId: string) => api.delete(`/feedback/${emailId}/feedback`),
  getStats: () => api.get('/feedback/stats'),
};

// Comment APIs
export const commentAPI = {
  get: (emailId: string) => api.get(`/comments/${emailId}/comment`),
  update: (emailId: string, comment: string, includeInAi: boolean) =>
    api.post(`/comments/${emailId}/comment`, { comment, include_in_ai: includeInAi }),
  delete: (emailId: string) => api.delete(`/comments/${emailId}/comment`),
};

// Prompt APIs
export const promptAPI = {
  list: (params?: { category?: string; active_only?: boolean; language?: string }) =>
    api.get('/prompts', { params }),
  getById: (id: string) => api.get(`/prompts/${id}`),
  getByCategory: (category: string, language: string = 'de') =>
    api.get(`/prompts/category/${category}`, { params: { language } }),
  getSystemPrompt: () => api.get('/prompts/system/current'),
  create: (data: any) => api.post('/prompts', data),
  update: (id: string, data: any) => api.put(`/prompts/${id}`, data),
  delete: (id: string) => api.delete(`/prompts/${id}`),
  activate: (id: string) => api.post(`/prompts/${id}/activate`),
  createSystem: (data: any) => api.post('/prompts/system', data),
  updateSystem: (id: string, data: any) => api.put(`/prompts/system/${id}`, data),
};

export default api;
